import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authService } from '../services/authService'

interface User {
  id: string // UUID from backend
  email: string
  username?: string
  organization_id?: number
  dealer_id?: number
  mine_postnummer?: string
  mine_organisasjoner?: string | null
  is_active: boolean
  is_verified: boolean
  roles: string[]
  created_at: string
  last_login_at?: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  error: string | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; user?: User; error?: string }>
  register: (email: string, password: string, username?: string, announcement_id?: number, contact_phone?: string, company_name?: string) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>
  fetchCurrentUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Try to fetch current user on mount (if access token exists)
    fetchCurrentUser()
  }, [])

  async function fetchCurrentUser() {
    try {
      // If no access token in memory (e.g. after page refresh), try to restore session
      if (!authService.getAccessToken()) {
        try {
          await authService.refreshAccessToken()
        } catch {
          // No valid refresh token cookie — user is not logged in
          setLoading(false)
          return
        }
      }

      const userData = await authService.getCurrentUser()
      setUser(userData)
      setError(null)
    } catch (error) {
      console.error('Failed to fetch user:', error)
      setUser(null)
      authService.clearTokens()
    } finally {
      setLoading(false)
    }
  }

  async function login(email: string, password: string) {
    setError(null)
    try {
      await authService.login(email, password)
      const userData = await authService.getCurrentUser()
      setUser(userData)
      return { success: true, user: userData }
    } catch (error: any) {
      setError(error.message)
      return { success: false, error: error.message }
    }
  }

  async function register(email: string, password: string, username?: string, announcement_id?: number, contact_phone?: string, company_name?: string) {
    setError(null)
    try {
      await authService.register(email, password, username || null, announcement_id, contact_phone, company_name)
      // Auto-login after registration
      await login(email, password)
      return { success: true }
    } catch (error: any) {
      setError(error.message)
      return { success: false, error: error.message }
    }
  }

  async function logout() {
    try {
      await authService.logout()
      setUser(null)
      setError(null)
    } catch (error) {
      console.error('Logout error:', error)
      // Still clear user even if logout fails
      setUser(null)
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    setError(null)
    try {
      await authService.changePassword(currentPassword, newPassword)
      return { success: true }
    } catch (error: any) {
      setError(error.message)
      return { success: false, error: error.message }
    }
  }

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    changePassword,
    fetchCurrentUser
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
